import { useQuery } from '@tanstack/react-query'
import { LogOut, Package, Settings, UserRound, Users } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { Link, matchPath, Outlet, useLocation } from 'react-router'

import { useMe } from '@/auth/queries'
import { useSignOut } from '@/auth/use-sign-out'
import { adminFamilyDetailOptions } from '@/client/@tanstack/react-query.gen'
import { AdminAccountSheet } from '@/components/features/admin-account-sheet'
import {
  BottomNavBar,
  type BottomNavBarBack,
  type BottomNavBarLinkItem,
} from '@/components/features/bottom-nav-bar'
import { useIsDesktop } from '@/components/features/use-media-query'
import { ModeToggle } from '@/components/mode-toggle'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function AppLayout(): ReactElement {
  const me = useMe()
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const [accountOpen, setAccountOpen] = useState(false)
  const account = me.data

  const { signOut, isPending } = useSignOut()

  const deviceMatch = matchPath(
    '/admin/families/:familyId/devices/:deviceId',
    location.pathname,
  )
  const familyDetailMatch = matchPath('/admin/families/:familyId', location.pathname)
  const deviceFamilyId = deviceMatch?.params.familyId
  const family = useQuery({
    ...adminFamilyDetailOptions({ path: { family_id: deviceFamilyId ?? '' } }),
    enabled: !isDesktop && deviceMatch !== null,
  })

  const isAdminPath = (path: string): boolean =>
    matchPath({ path, end: false }, location.pathname) !== null

  const back: BottomNavBarBack | undefined =
    deviceMatch !== null
      ? {
          to: `/admin/families/${deviceFamilyId}`,
          label: family.data?.name ?? 'Family',
        }
      : familyDetailMatch !== null
        ? { to: '/admin/families', label: 'Families' }
        : undefined

  const tabs: BottomNavBarLinkItem[] =
    account?.role === 'admin'
      ? [
          {
            kind: 'link',
            label: 'Families',
            to: '/admin/families',
            icon: Users,
            current: isAdminPath('/admin/families'),
          },
          {
            kind: 'link',
            label: 'Releases',
            to: '/admin/agent-releases',
            icon: Package,
            current: isAdminPath('/admin/agent-releases'),
          },
          {
            kind: 'link',
            label: 'Settings',
            to: '/admin/settings',
            icon: Settings,
            current: isAdminPath('/admin/settings'),
          },
        ]
      : []

  return (
    <div className="bg-background min-h-screen">
      {isDesktop && (
        <header className="border-b pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[env(safe-area-inset-top)]">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="font-semibold">
                TradVal Monitor
              </Link>
              {account?.role === 'admin' && (
                <nav className="text-muted-foreground flex gap-3 text-sm">
                  <Link to="/admin/families" className="hover:text-foreground">
                    Families
                  </Link>
                  <Link to="/admin/agent-releases" className="hover:text-foreground">
                    Releases
                  </Link>
                  <Link to="/admin/settings" className="hover:text-foreground">
                    Settings
                  </Link>
                </nav>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ModeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    {account?.name ?? account?.email ?? 'Account'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {account && (
                    <>
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-0.5">
                          <span className="text-sm font-medium">{account.name}</span>
                          <span className="text-muted-foreground text-xs">{account.email}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onSelect={signOut} disabled={isPending}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
      )}
      <main
        className={
          isDesktop
            ? 'p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]'
            : 'p-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(3.5rem+env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]'
        }
      >
        <Outlet />
      </main>
      {!isDesktop && (
        <>
          <BottomNavBar
            back={back}
            items={[
              ...tabs,
              {
                kind: 'button',
                label: 'Account',
                icon: UserRound,
                onClick: () => setAccountOpen(true),
              },
            ]}
          />
          <AdminAccountSheet open={accountOpen} onOpenChange={setAccountOpen} />
        </>
      )}
    </div>
  )
}
